import { TemplateVariables } from '@/lib/slack/messages/welcome-messages'
import { supabase } from '@/integrations/supabase/client'

// Leadership ('main') and reporting were retired from the channel set on
// 2026-09-02. Existing client channels stay live and their columns are kept,
// but nothing can create new ones. meetingsBooked and websiteIdentification
// replace them and are created on demand only, one at a time, never as part of
// the default set.
type ChannelKey = 'sales' | 'positive' | 'neutralNegative' | 'meetingsBooked' | 'websiteIdentification'

// Map UI channel keys to Edge Function channel keys
type EdgeFunctionChannelKey = 'general' | 'positive' | 'neutralNegative' | 'meetingsBooked' | 'websiteIdentification'

function mapChannelKeyToEdgeFunction(key: ChannelKey): EdgeFunctionChannelKey {
  const mapping: Record<ChannelKey, EdgeFunctionChannelKey> = {
    sales: 'general',
    positive: 'positive',
    neutralNegative: 'neutralNegative',
    meetingsBooked: 'meetingsBooked',
    websiteIdentification: 'websiteIdentification'
  }
  return mapping[key]
}

function mapEdgeFunctionKeyToUI(key: EdgeFunctionChannelKey): ChannelKey {
  const mapping: Record<EdgeFunctionChannelKey, ChannelKey> = {
    general: 'sales',
    positive: 'positive',
    neutralNegative: 'neutralNegative',
    meetingsBooked: 'meetingsBooked',
    websiteIdentification: 'websiteIdentification'
  }
  return mapping[key]
}

export interface CreateClientChannelsParams {
  clientId: string
  clientName: string
  variables?: TemplateVariables
  /** Optional: Slack user emails to add to channels (e.g., sales team emails) */
  memberEmails?: string[]
  /** Optional: Per-channel email assignments for role-based channel membership */
  channelAssignments?: Partial<Record<ChannelKey, string[]>>
  /** Optional: Custom channel names to use instead of auto-generated ones */
  channelNames?: Partial<Record<ChannelKey, string>>
}

export interface CreateClientChannelsResult {
  success: boolean
  channels: Partial<Record<ChannelKey, { id: string; name: string }>>
  error?: string
}

/**
 * Resolve Slack Bot token from environment.
 * - Node/Edge: process.env.SLACK_BOT_TOKEN / Deno.env.get('SLACK_BOT_TOKEN')
 * - Browser dev (rare): localStorage fallback (same pattern as SlackWrapper helper)
 */
function getSlackBotToken(): string | null {
  // Deno (Supabase Edge Functions)
  // @ts-ignore - Deno typing not available in browser build
  if (typeof Deno !== 'undefined' && Deno?.env?.get) {
    // @ts-ignore
    const t = Deno.env.get('SLACK_BOT_TOKEN') as string | null
    if (t) return t
  }
  if (typeof process !== 'undefined' && process?.env?.SLACK_BOT_TOKEN) {
    return process.env.SLACK_BOT_TOKEN as string
  }
  if (typeof window !== 'undefined') {
    try { return localStorage.getItem('slack_bot_token') } catch { /* ignore */ }
  }
  return null
}

async function slackApi<T = any>(endpoint: string, body: any): Promise<T> {
  const token = getSlackBotToken()
  if (!token) throw new Error('Slack bot token not configured')

  const res = await fetch(`https://slack.com/api/${endpoint}`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body)
  })

  const data = await res.json()
  if (!data?.ok) {
    throw new Error(`Slack API ${endpoint} failed: ${data?.error || res.statusText}`)
  }
  return data as T
}

async function ensureChannel(name: string): Promise<{ id: string; name: string }> {
  // conversations.create will error if it exists; we try find then create
  try {
    const created = await slackApi<{ channel: { id: string; name: string } }>('conversations.create', { name })
    return { id: created.channel.id, name: created.channel.name }
  } catch (e: any) {
    if (String(e?.message || '').includes('name_taken')) {
      const token = getSlackBotToken()
      const listRes = await fetch('https://slack.com/api/conversations.list', {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      })
      const listData = await listRes.json()
      const match = listData?.channels?.find((c: any) => c?.name === name)
      if (match) return { id: match.id, name: match.name }
    }
    throw e
  }
}

async function inviteMembers(channelId: string, userIds: string[]): Promise<void> {
  if (!userIds?.length) return
  await slackApi('conversations.invite', { channel: channelId, users: userIds.join(',') })
}

async function lookupUserIdsByEmails(emails: string[] | undefined): Promise<string[]> {
  if (!emails?.length) {
    console.log('📧 [channel-service] No emails provided for lookup')
    return []
  }
  
  const token = getSlackBotToken()
  if (!token) {
    console.error('❌ [channel-service] No Slack bot token available - cannot look up users')
    return []
  }

  console.log(`📧 [channel-service] Looking up ${emails.length} emails:`, emails)

  const results: string[] = []
  for (const email of emails) {
    try {
      const resp = await fetch(`https://slack.com/api/users.lookupByEmail?email=${encodeURIComponent(email)}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      })
      const data = await resp.json()
      
      if (data?.ok && data?.user?.id) {
        console.log(`✅ [channel-service] Found Slack user for ${email}: ${data.user.id} (${data.user.name || 'no name'})`)
        results.push(data.user.id)
      } else {
        console.warn(`⚠️ [channel-service] Slack lookup failed for ${email}:`, data?.error || 'Unknown error', data)
      }
    } catch (err) {
      console.error(`❌ [channel-service] Slack lookupByEmail error for ${email}:`, err)
    }
  }
  
  console.log(`📊 [channel-service] Successfully resolved ${results.length}/${emails.length} emails to Slack user IDs`)
  if (results.length < emails.length) {
    const failedCount = emails.length - results.length
    console.warn(`⚠️ [channel-service] ${failedCount} email(s) failed to resolve to Slack user IDs`)
  }
  
  return results
}

function clientSlug(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
}

export async function createClientChannels(
  clientId: string,
  clientName: string,
  options: Omit<CreateClientChannelsParams, 'clientId' | 'clientName'> = {}
): Promise<CreateClientChannelsResult> {
  const logPrefix = `🧵 [channel-service] client=${clientId}(${clientName})`
  try {
    console.log(`${logPrefix} creating channels via Edge Function`)

    // Map UI channel names to Edge Function channel names
    const edgeFunctionChannelNames: Partial<Record<EdgeFunctionChannelKey, string>> = {}
    if (options.channelNames) {
      Object.entries(options.channelNames).forEach(([key, value]) => {
        const edgeKey = mapChannelKeyToEdgeFunction(key as ChannelKey)
        if (value) edgeFunctionChannelNames[edgeKey] = value
      })
    }

    // Map UI channel assignments to Edge Function channel assignments
    const edgeFunctionChannelAssignments: Partial<Record<EdgeFunctionChannelKey, string[]>> = {}
    if (options.channelAssignments) {
      Object.entries(options.channelAssignments).forEach(([key, emails]) => {
        const edgeKey = mapChannelKeyToEdgeFunction(key as ChannelKey)
        if (emails) edgeFunctionChannelAssignments[edgeKey] = emails
      })
    }

    // Call new Edge Function
    const { data, error } = await supabase.functions.invoke('create-client-slack-channels', {
      body: {
        clientId,
        clientName,
        channelNames: Object.keys(edgeFunctionChannelNames).length > 0 ? edgeFunctionChannelNames : undefined,
        channelAssignments: Object.keys(edgeFunctionChannelAssignments).length > 0 ? edgeFunctionChannelAssignments : undefined,
        variables: options.variables
      }
    })

    if (error) {
      console.error(`${logPrefix} Edge Function error:`, error)
      throw new Error(error.message || 'Failed to create channels via Edge Function')
    }

    if (!data?.success) {
      throw new Error(data?.error || 'Edge Function returned unsuccessful result')
    }

    // Map Edge Function response back to UI format
    const created: Partial<Record<ChannelKey, { id: string; name: string }>> = {}
    if (data.channels) {
      Object.entries(data.channels).forEach(([key, value]) => {
        const uiKey = mapEdgeFunctionKeyToUI(key as EdgeFunctionChannelKey)
        if (value && typeof value === 'object' && 'id' in value && 'name' in value) {
          created[uiKey] = value as { id: string; name: string }
        }
      })
    }

    console.log(`${logPrefix} completed successfully via Edge Function`)
    return { success: true, channels: created }
  } catch (error: any) {
    console.error(`${logPrefix} failed:`, error)
    return { success: false, channels: {}, error: error?.message || 'Unknown error' }
  }
}

/**
 * Create a single Slack channel with welcome message and members
 */
/**
 * Send welcome message to an existing Slack channel
 */
export async function sendWelcomeMessage(
  channelName: string,
  clientName: string = 'BrandNew'
): Promise<{ success: boolean; error?: string }> {
  try {
    console.log(`📤 [channel-service] Sending welcome message to #${channelName}...`)
    
    const { data, error } = await supabase.functions.invoke('post-welcome-message', {
      body: {
        channelName,
        clientName
      }
    })
    
    if (error) {
      console.error(`❌ [channel-service] Error sending welcome message:`, error)
      return { success: false, error: error.message }
    }
    
    if (data?.success) {
      console.log(`✅ [channel-service] Welcome message sent to #${channelName}`)
      return { success: true }
    }
    
    return { success: false, error: data?.error || 'Unknown error' }
  } catch (error: any) {
    console.error(`❌ [channel-service] Failed to send welcome message:`, error)
    return { success: false, error: error?.message || 'Unknown error' }
  }
}

export async function createSingleChannel(
  clientId: string,
  clientName: string,
  channelType: ChannelKey,
  options: {
    variables?: TemplateVariables
    memberEmails?: string[]
    channelName?: string
  } = {}
): Promise<{ 
  success: boolean; 
  channel?: { id: string; name: string }; 
  error?: string; 
  membersAdded?: number; 
  membersAttempted?: number;
  verification?: {
    verified: string[];
    missing: string[];
    verifiedEmails: string[];
    missingEmails: string[];
    expected: number;
    manualInvitesNeeded?: Array<{
      email: string;
      channelId: string;
      channelName: string;
      adminInviteUrl: string;
      instructions: string;
    }>;
  }
}> {
  const logPrefix = `🧵 [channel-service] client=${clientId}(${clientName}) channel=${channelType}`
  try {
    console.log(`${logPrefix} creating single channel via Edge Function`)

    // Map to Edge Function channel key
    const edgeChannelType = mapChannelKeyToEdgeFunction(channelType)

    // Build channel assignments for just this channel
    const channelAssignments: Record<string, string[]> = {
      [edgeChannelType]: options.memberEmails || []
    }

    // Build channel names object - ALWAYS include the channel type to indicate single channel mode
    // The Edge Function will use default generated name if no custom name is provided
    // This is critical: if channelNames is undefined, Edge Function assumes bulk mode (all 5 channels)
    const channelNames: Record<string, string> = {
      [edgeChannelType]: options.channelName || '' // Always include the key, even if empty string
    }

    // Call new Edge Function - it will only create the requested channel
    // IMPORTANT: Always send channelNames (never undefined) to ensure single channel mode
    const { data, error } = await supabase.functions.invoke('create-client-slack-channels', {
      body: {
        clientId,
        clientName,
        channelNames, // Always send channelNames to indicate single channel mode
        channelAssignments,
        variables: options.variables
      }
    })

    if (error) {
      console.error(`${logPrefix} Edge Function error:`, error)
      throw new Error(error.message || 'Failed to create channel via Edge Function')
    }

    if (!data?.success) {
      throw new Error(data?.error || 'Edge Function returned unsuccessful result')
    }

    // Get the specific channel we created (map back from Edge Function key)
    const created = data.channels[edgeChannelType]
    if (!created) {
      throw new Error(`Channel ${channelType} not found in Edge Function response`)
    }

    // Extract verification data from response
    const verification = data.verification?.[edgeChannelType]
    const membersAdded = verification?.verified.length || 0
    const membersAttempted = options.memberEmails?.length || 0

    // Debug: Log verification data received from Edge Function
    console.log(`${logPrefix} verification data from Edge Function:`, {
      hasVerification: !!verification,
      verificationKeys: verification ? Object.keys(verification) : [],
      verified: verification?.verified?.length || 0,
      missing: verification?.missing?.length || 0,
      verifiedEmails: verification?.verifiedEmails?.length || 0,
      missingEmails: verification?.missingEmails?.length || 0,
      manualInvitesNeeded: verification?.manualInvitesNeeded?.length || 0,
      fullVerification: verification ? JSON.stringify(verification, null, 2) : 'none'
    });

    console.log(`${logPrefix} completed successfully via Edge Function`)
    return { 
      success: true, 
      channel: created,
      membersAdded,
      membersAttempted,
      verification: verification ? {
        ...verification,
        expected: membersAttempted
      } : undefined
    }
  } catch (error: any) {
    console.error(`${logPrefix} failed:`, error)
    return { success: false, error: error?.message || 'Unknown error' }
  }
}

export default {
  createClientChannels,
  createSingleChannel
}




